import { useState, useEffect, useLayoutEffect } from 'react'
import { DatePosition } from '../helpers/common-types';
import { throttle, debounce } from 'lodash';
import { useAfterPaintEffect } from './hooks';

export const useCurrentScrolledItem = (firstEventInRange: string, dateOffset: number) => {
    const [currentScrolledItem, setCurrentScrolledItem] = useState(firstEventInRange);
    const [browserWidth, setBrowserWidth] = useState(0);
    const [datePositions, setDatePositions] = useState<DatePosition[]>([]);

    useEffect(() => {
        document.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);
        return () => {
            document.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        }
    }, [currentScrolledItem, datePositions]);

    // Reset when the range changes before render
    useEffect(() => {
        setCurrentScrolledItem('');
    }, [firstEventInRange]);

    useAfterPaintEffect(() => {
        // we need to wait until the whole tree has re-rendered, so call after paint, after data has updated
        setCurrentScrolledItem(firstEventInRange);
        setDatePositions(updateDatePositions());
    }, [firstEventInRange]);

    /*
     * Reference the positions to find the date currently scrolled to
     */
    const evaluateScroll = () => {
        const dateString = getDateFromScrollPosition(datePositions, currentScrolledItem === "", dateOffset);
        if (dateString && dateString !== currentScrolledItem) {
            setCurrentScrolledItem(dateString);
        }
    }

    const evaluateResize = () => {
        // Browser width is measured in order to trigger side effects in list day items when it changes, without new resize listeners
        setBrowserWidth(window.innerWidth);
        setDatePositions(updateDatePositions());
    }

    const handleScroll = throttle(evaluateScroll, 50);

    const handleResize = debounce(evaluateResize, 250);

    return { currentScrolledItem, browserWidth };
}

const getDateFromScrollPosition = (currentDatePositions: DatePosition[], setInitial: boolean, dateOffset: number) => {
    const scrollPos = window.pageYOffset;
    for (let i = 0; i < currentDatePositions.length; i++) {
        const date = currentDatePositions[i];
        if (setInitial && i == 0) return date.dateString;
        // arbitrary buffer -- based on sticky filter height but you can tweak as needed by changing the integer.
        const adjustedOffset = date.offsetTop - dateOffset - 30;
        if (adjustedOffset < scrollPos && scrollPos <= adjustedOffset + date.height) {
            return date.dateString;
        }
    }
    return;
}

const updateDatePositions = () => {
    const updatedDatePositions: DatePosition[] = [];
    const dateItems = [].slice.call(document.querySelectorAll('[data-date]')) as HTMLElement[];
    dateItems.forEach(dateItem => {
        updatedDatePositions.push({
            dateString: dateItem.getAttribute('data-date') || "",
            offsetTop: dateItem.offsetTop,
            height: dateItem.offsetHeight
        })
    });
    return updatedDatePositions;
}